import { useEffect, lazy, Suspense } from 'react'
import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useThemeStore } from '@/stores/themeStore'
import { useAuthStore } from '@/stores/authStore'
import MainLayout from '@/components/layout/MainLayout'

// Lazy-loaded pages
const HomePage = lazy(() => import('@/pages/HomePage'))
const LoginPage = lazy(() => import('@/pages/LoginPage'))
const SignupPage = lazy(() => import('@/pages/SignupPage'))
const ArticlePage = lazy(() => import('@/pages/ArticlePage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
const SearchPage = lazy(() => import('@/pages/SearchPage'))
const MessagesPage = lazy(() => import('@/pages/MessagesPage'))
const NotificationsPage = lazy(() => import('@/pages/NotificationsPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))

// Admin pages
const AdminLayout = lazy(() => import('@/pages/admin/AdminLayout'))
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'))
const AdminUsers = lazy(() => import('@/pages/admin/AdminUsers'))
const AdminFeeds = lazy(() => import('@/pages/admin/AdminFeeds'))
const AdminCategories = lazy(() => import('@/pages/admin/AdminCategories'))
const AdminAds = lazy(() => import('@/pages/admin/AdminAds'))
const AdminPayouts = lazy(() => import('@/pages/admin/AdminPayouts'))
const AdminSettings = lazy(() => import('@/pages/admin/AdminSettings'))
const AdminAnalytics = lazy(() => import('@/pages/admin/AdminAnalytics'))
const ContentEngine = lazy(() => import('@/pages/admin/ContentEngine'))

// Placeholder for remaining routes
const PlaceholderPage = lazy(() =>
  Promise.resolve({
    default: () => (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
            Coming Soon
          </h2>
          <p style={{ color: 'var(--text-secondary)' }}>This page is under construction.</p>
        </div>
      </div>
    ),
  })
)

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
})

// Loading fallback
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <div className="flex flex-col items-center gap-3">
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{
          borderColor: 'var(--border-primary)',
          borderTopColor: 'var(--accent)',
        }} />
        <p className="text-sm" style={{ color: 'var(--text-tertiary)' }}>Loading...</p>
      </div>
    </div>
  )
}

function AppContent() {
  const theme = useThemeStore((s) => s.theme)
  const initialize = useAuthStore((s) => s.initialize)

  // Initialize auth on mount
  useEffect(() => {
    const cleanup = initialize()
    return cleanup
  }, [initialize])

  // Apply theme to document
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme)
    if (theme === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
  }, [theme])

  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Auth pages — no layout */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />

          {/* Main layout routes */}
          <Route element={<MainLayout />}>
            <Route index element={<HomePage />} />
            <Route path="/article/:id" element={<ArticlePage />} />
            <Route path="/category/:slug" element={<PlaceholderPage />} />
            <Route path="/profile/:username" element={<ProfilePage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/search" element={<SearchPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<AdminDashboard />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="feeds" element={<AdminFeeds />} />
              <Route path="categories" element={<AdminCategories />} />
              <Route path="ads" element={<AdminAds />} />
              <Route path="payouts" element={<AdminPayouts />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="content-engine" element={<ContentEngine />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppContent />
    </QueryClientProvider>
  )
}
